import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiKeyService } from '../../services/apiKeyService';

const EditApiKey = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [permissions, setPermissions] = useState([]);
    const [expiresInDays, setExpiresInDays] = useState(null);
    const [isActive, setIsActive] = useState(true);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [key, setKey] = useState(null);

    // Permission options
    const permissionOptions = [
        { value: 'send-text', label: 'Send Text Messages' },
        { value: 'send-template', label: 'Send Templates' },
        { value: 'send-media', label: 'Send Media' },
        { value: 'send-buttons', label: 'Send Interactive Buttons' },
        { value: 'upload-media', label: 'Upload Media' }
    ];

    useEffect(() => {
        loadKey();
    }, [id]);

    const loadKey = async () => {
        try {
            setLoading(true);
            const response = await apiKeyService.getApiKey(id);
            const keyData = response.data;

            setKey(keyData);
            setName(keyData.name);
            setPermissions(keyData.permissions || []);
            setIsActive(keyData.isActive);

            // Calculate days remaining if expiration exists
            if (keyData.expiresAt) {
                const now = new Date();
                const expiryDate = new Date(keyData.expiresAt);
                const daysRemaining = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
                setExpiresInDays(daysRemaining > 0 ? daysRemaining : 1);
            } else {
                setExpiresInDays(null);
            }

            setError(null);
        } catch (err) {
            setError('Failed to load API key: ' + (err.message || 'Unknown error'));
            console.error('Error loading API key:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!name || permissions.length === 0) {
            setError('Please fill out all required fields.');
            return;
        }

        try {
            setSaving(true);
            setError(null);

            await apiKeyService.updateApiKey(id, {
                name,
                permissions,
                expiresInDays: expiresInDays || null,
                isActive
            });

            alert('API key updated successfully');
            navigate(`/api-keys/${id}`);
        } catch (err) {
            setError('Failed to update API key: ' + (err.message || 'Unknown error'));
            console.error('Error updating API key:', err);
        } finally {
            setSaving(false);
        }
    };

    const handlePermissionChange = (permission) => {
        if (permissions.includes(permission)) {
            setPermissions(permissions.filter(p => p !== permission));
        } else {
            setPermissions([...permissions, permission]);
        }
    };

    const handleSelectAllPermissions = () => {
        if (permissions.length === permissionOptions.length) {
            setPermissions([]);
        } else {
            setPermissions(permissionOptions.map(p => p.value));
        }
    };

    if (loading) {
        return <div className="p-4">Loading API key...</div>;
    }

    if (!key) {
        return (
            <div className="p-4 text-red-600">
                <p>API key not found or you don't have permission to edit it.</p>
                <div className="mt-4">
                    <button
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                        onClick={() => navigate('/api-keys')}
                    >
                        Back to API Keys
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4">
            <h2 className="text-xl font-bold mb-4">Edit API Key</h2>

            <form onSubmit={handleSubmit} className="bg-white p-6 rounded shadow-md">
                {error && (
                    <div className="bg-red-100 border border-red-300 text-red-600 p-3 rounded mb-4">
                        {error}
                    </div>
                )}

                <div className="mb-4">
                    <label className="block text-gray-700 mb-2" htmlFor="name">
                        API Key Name <span className="text-red-600">*</span>
                    </label>
                    <input
                        id="name"
                        type="text"
                        className="w-full px-3 py-2 border rounded"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="E.g., ERP Integration, CRM App"
                        required
                    />
                </div>

                <div className="mb-4">
                    <label className="block text-gray-700 mb-2">
                        Permissions <span className="text-red-600">*</span>
                    </label>
                    <div className="flex mb-2">
                        <button
                            type="button"
                            className="text-sm text-blue-600 hover:underline"
                            onClick={handleSelectAllPermissions}
                        >
                            {permissions.length === permissionOptions.length ? 'Deselect All' : 'Select All'}
                        </button>
                    </div>
                    {permissionOptions.map((option) => (
                        <div key={option.value} className="flex items-center mb-2">
                            <input
                                id={`permission-${option.value}`}
                                type="checkbox"
                                className="mr-2"
                                checked={permissions.includes(option.value)}
                                onChange={() => handlePermissionChange(option.value)}
                            />
                            <label htmlFor={`permission-${option.value}`}>
                                {option.label}
                            </label>
                        </div>
                    ))}
                </div>

                <div className="mb-4">
                    <label className="block text-gray-700 mb-2" htmlFor="expiresInDays">
                        Update Expiration
                    </label>
                    <select
                        id="expiresInDays"
                        className="w-full px-3 py-2 border rounded"
                        value={expiresInDays || ''}
                        onChange={(e) => setExpiresInDays(e.target.value ? parseInt(e.target.value) : null)}
                    >
                        <option value="">Never expires</option>
                        <option value="30">30 days from now</option>
                        <option value="90">90 days from now</option>
                        <option value="180">6 months from now</option>
                        <option value="365">1 year from now</option>
                        <option value="730">2 years from now</option>
                    </select>
                    <p className="text-sm text-gray-500 mt-1">
                        {key.expiresAt
                            ? `Current expiration: ${new Date(key.expiresAt).toLocaleDateString()}`
                            : 'Currently set to never expire'}
                    </p>
                </div>

                <div className="mb-6">
                    <label className="flex items-center text-gray-700">
                        <input
                            type="checkbox"
                            className="mr-2"
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                        />
                        Active
                    </label>
                    <p className="text-sm text-gray-500 mt-1">
                        Inactive keys cannot be used for API requests.
                    </p>
                </div>

                <div className="flex justify-end">
                    <button
                        type="button"
                        className="px-4 py-2 border rounded mr-2 hover:bg-gray-100"
                        onClick={() => navigate(`/api-keys/${id}`)}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EditApiKey; 